import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PropsWithChildren, useState } from 'react';

type ComponentProps = {
  hasFocus?: boolean;
  alwaysOnTooltip?: string;
  tooltip?: string;
};

type Props = PropsWithChildren<TypographyProps & ComponentProps>;

export const TypographyOverflow = (props: Props) => {
  const { children, tooltip, alwaysOnTooltip, hasFocus, ...typographyProps } = props;

  const [overflows, setOverflows] = useState(false);
  const [elementText, setElementText] = useState<string | null>(null);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const tooltipText = tooltip ?? elementText;

  const needsTooltip = overflows || Boolean(alwaysOnTooltip);
  const shouldRenderTooltip = Boolean(needsTooltip && (isMouseOver || hasFocus));

  const callbackRef = (el: HTMLSpanElement | null) => {
    if (!el) {
      setElementText(null);
      setOverflows(false);
      return;
    }

    setElementText(el.innerText);
    setOverflows(el.clientWidth < el.scrollWidth);
  };

  const onMouseEnter = () => {
    setIsMouseOver(true);
  };

  const onMouseLeave = () => {
    setIsMouseOver(false);
  };

  const mergedTypographyProps = deepmerge(
    {
      sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' },
      variant: 'body2',
    },
    typographyProps
  ) satisfies TypographyProps;

  const typographyWrapper = (
    <Typography className="typography-overflow" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={callbackRef} {...mergedTypographyProps}>
      {children}
    </Typography>
  );

  const TitleComponent = () => (
    <>
      {overflows && tooltipText && <Typography variant="body2">{tooltipText}</Typography>}
      {alwaysOnTooltip && <Typography variant="body2">{alwaysOnTooltip}</Typography>}
    </>
  );

  return needsTooltip ? (
    <Tooltip open={shouldRenderTooltip} title={<TitleComponent />}>
      {typographyWrapper}
    </Tooltip>
  ) : (
    typographyWrapper
  );
};
